import { useState, useEffect, useMemo, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTags } from "@/features/admin/queries/useTags";
import { addTagToFile, removeTagFromFile } from "@/services/api/files";
import { toast } from "sonner";
import type { FileItem } from "@/features/categories/types";
import type { Tag } from "@/services/api/tags/types";
import { ApiClientError } from "@/services/api/client";

interface UseEditTagsProps {
  open: boolean;
  file: FileItem;
}

export function useEditTags({ open, file }: UseEditTagsProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [removedTagIds, setRemovedTagIds] = useState<Set<string | number>>(
    new Set()
  );
  const [addedTagIds, setAddedTagIds] = useState<Set<string | number>>(
    new Set()
  );
  const queryClient = useQueryClient();

  const { data: availableTags = [], isLoading: tagsLoading } = useTags({
    enabled: open,
  });

  const assignedTags = useMemo<Tag[]>(() => {
    return availableTags.filter(
      (tag) =>
        tag.name &&
        (addedTagIds.has(tag.id) ||
          (file.tags.some((tagName) => tag.name === tagName) &&
            !removedTagIds.has(tag.id)))
    );
  }, [availableTags, file.tags, addedTagIds, removedTagIds]);

  const assignedTagIdsSet = useMemo<Set<string | number>>(() => {
    return new Set(assignedTags.map((tag) => tag.id));
  }, [assignedTags]);

  useEffect(() => {
    if (open) {
      setRemovedTagIds(new Set());
      setAddedTagIds(new Set());
    } else {
      setSearchQuery("");
      setRemovedTagIds(new Set());
      setAddedTagIds(new Set());
    }
  }, [open]);

  const filteredTags = useMemo<Tag[]>(() => {
    const normalizedQuery = searchQuery.toLowerCase();
    return availableTags.filter(
      (tag) =>
        tag.isActive &&
        !assignedTagIdsSet.has(tag.id) &&
        (tag.name?.toLowerCase().includes(normalizedQuery) ?? false)
    );
  }, [availableTags, assignedTagIdsSet, searchQuery]);

  const removeTagMutation = useMutation({
    mutationFn: async (tagId: number) => {
      await removeTagFromFile(file.id, tagId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
    },
    onError: (error: unknown, tagId: number) => {
      setRemovedTagIds((prev) => {
        const next = new Set(prev);
        next.delete(tagId);
        return next;
      });

      const errorMessage =
        error instanceof ApiClientError
          ? error.body?.detail || error.body?.message || error.message
          : error instanceof Error
            ? error.message
            : "An error occurred while removing the tag.";

      toast.error("Failed to remove tag", {
        description: errorMessage,
      });
    },
  });

  const addTagMutation = useMutation({
    mutationFn: async (tagId: number) => {
      await addTagToFile(file.id, tagId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
    },
    onError: (error: unknown, tagId: number) => {
      setAddedTagIds((prev) => {
        const next = new Set(prev);
        next.delete(tagId);
        return next;
      });

      const errorMessage =
        error instanceof ApiClientError
          ? error.body?.detail || error.body?.message || error.message
          : error instanceof Error
            ? error.message
            : "An error occurred while adding the tag.";

      toast.error("Failed to add tag", {
        description: errorMessage,
      });
    },
  });

  const handleTagAdd = useCallback(
    (tagId: string | number) => {
      const numericTagId = Number(tagId);
      if (isNaN(numericTagId)) return;

      if (assignedTagIdsSet.has(tagId)) return;

      // If tag was previously removed, remove it from removedTagIds
      // This ensures clean state, though the assignedTags logic handles it
      const wasRemoved = removedTagIds.has(tagId);
      if (wasRemoved) {
        setRemovedTagIds((prev) => {
          const next = new Set(prev);
          next.delete(tagId);
          return next;
        });
      }

      setAddedTagIds((prev) => {
        const next = new Set(prev);
        next.add(tagId);
        return next;
      });
      setSearchQuery("");

      addTagMutation.mutate(numericTagId);
    },
    [assignedTagIdsSet, addTagMutation, removedTagIds]
  );

  const handleTagRemove = useCallback(
    (tagId: string | number) => {
      const numericTagId = Number(tagId);
      if (isNaN(numericTagId)) return;

      const wasJustAdded = addedTagIds.has(tagId);

      if (wasJustAdded) {
        setAddedTagIds((prev) => {
          const next = new Set(prev);
          next.delete(tagId);
          return next;
        });
      } else {
        setRemovedTagIds((prev) => {
          const next = new Set(prev);
          next.add(tagId);
          return next;
        });
        removeTagMutation.mutate(numericTagId);
      }
    },
    [addedTagIds, removeTagMutation, availableTags]
  );

  return {
    assignedTags,
    filteredTags,
    tagsLoading,
    searchQuery,
    setSearchQuery,
    handleTagAdd,
    handleTagRemove,
    isPending: addTagMutation.isPending || removeTagMutation.isPending,
    hasAvailableTags: availableTags.length > 0,
  };
}
